import clsx from 'clsx'
import { ImageOffIcon } from 'lucide-react'
import React, { useContext, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ProductCarouselContext } from './context'

interface ProductCarouselItemProps {
  thumbnailUrl: string;
  title: string;
  url: string;
}

function usePortalElement () {
  const { portalId } = useContext(ProductCarouselContext)

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    document.getElementById(portalId)
  )

  /**
   * If the portal container is created by an ancestor React component, it may
   * not be present in the DOM when this component first mounts. We'll use a
   * layout effect to check for it one more time before this component is
   * first painted.
   */
  useLayoutEffect(() => {
    if (portalContainer) return

    setPortalContainer(document.getElementById(portalId))
  }, [portalId])

  return portalContainer
}

interface ItemThumbnailProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  title: string;
  url: string;
}

function ItemThumbnail ({ active, onClick, title, url, ...props }: ItemThumbnailProps) {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  return (
    <button
      {...props}
      aria-selected={active}
      className={clsx(
        'appearance-none h-full w-auto rounded-lg shrink-0 scroll-mx-4 bg-gray-100 relative focus-visible:outline-transparent',
        {
          'outline outline-2 -outline-offset-2 outline-thatch-500': active,
          'aspect-1': !loaded || error
        }
      )}
      data-carousel-tab=''
      data-loading={!loaded}
      onClick={onClick}
      role='tab'
      tabIndex={-1}
      type='button'
    >
      <img
        alt={title}
        className={clsx('h-full rounded-lg pointer-events-none transition', {
          'opacity-0 aspect-1': !loaded || error,
          'opacity-1': loaded && !error,
        })}
        onLoad={() => {
          setLoaded(true)
        }}
        onError={() => {
          setLoaded(false)
          setError(true)
        }}
        src={url}
      />
      {error && (
        <div className='absolute top-0 left-0 h-full w-full flex items-center justify-center text-gray-500 p-1'>
          <ImageOffIcon aria-label='Thumbnail image failed to load' size={20} />
        </div>
      )}
    </button>
  )
}

export function ProductCarouselItem ({ thumbnailUrl, title, url }: ProductCarouselItemProps) {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  const id = useId()

  const itemRef = useRef<HTMLDivElement>(null)

  const { activeItem, setActiveItem } = useContext(
    ProductCarouselContext
  )
  const portal = usePortalElement()

  return (
    <>
      <div
        aria-hidden={activeItem !== id}
        className='w-full h-full snap-center shrink-0 relative'
        data-carousel-item=''
        id={id}
        ref={itemRef}
        role='tabpanel'
        aria-roledescription='slide'
      >
        <img
          alt={title}
          className={clsx('object-contain w-full h-full object-center pointer-events-none transition', {
            'opacity-0': !loaded || error,
            'opacity-1': loaded && !error
          })}
          onLoad={() => {
            setLoaded(true)
          }}
          onError={() => {
            setLoaded(true)
            setError(true)
          }}
          src={url}
        />
        {error && (
          <div className='absolute top-0 left-0 h-full w-full flex items-center justify-center text-white p-8'>
            <div className='flex gap-2'>
              <ImageOffIcon aria-label='Image failed to load' className='shrink-0' size={32} />
              <div className='body mt-1.5'>{title}</div>
            </div>
          </div>
        )}
      </div>
      {portal &&
        createPortal(
          <ItemThumbnail
            url={thumbnailUrl}
            active={activeItem === id}
            onClick={(event) => {
              event.preventDefault()
              setActiveItem(id)
              itemRef.current?.scrollIntoView({
                behavior: 'instant',
                block: 'nearest',
                inline: 'nearest',
              })
            }}
            title={title}
          />,
          portal
        )}
    </>
  )
}

ProductCarouselItem.displayName = 'ProductCarousel.Item'
